<template>
  <!-- Deliberately a snackbar, not a modal dialog. The reminder is a nudge, not
       an interruption: a modal on every login would train people to dismiss it
       without reading, which is how the August gap happened in the first place. -->
  <v-snackbar
    :model-value="visible"
    :timeout="-1"
    location="bottom right"
    color="warning"
    multi-line
    class="mb-4"
  >
    <div class="font-weight-medium">{{ monthLabel }} is not closed</div>
    <div class="text-body-small">
      {{ outstanding }} of {{ total }} closing steps still to do.
    </div>
    <template v-slot:actions>
      <v-btn variant="text" @click="open">Open</v-btn>
      <v-btn variant="text" @click="dismiss">Later</v-btn>
    </template>
  </v-snackbar>
</template>

<script setup>
/**
 * Reminds whoever can close the books that last month is still open.
 *
 * Why it exists: the eight steps were only ever visible if you went looking for
 * them, so August 2026 sat unposted for a week and its cash sales were missing
 * from the Day Book without anyone noticing until the operator asked.
 *
 * Rules it follows:
 *  - only for users who can actually close (gl/accounting app + admin), so it
 *    never nags a viewer or a kitchen operator who cannot act on it;
 *  - only once the month is genuinely over;
 *  - "Later" snoozes for ONE DAY, not for the month (Maurizio, 2026-09-08). The
 *    whole point is not letting a month sit forgotten; a per-month dismissal
 *    would have let a click on the 1st silence it until the next month. The key
 *    is still per month so a new month never inherits an old snooze. Nothing
 *    here is authoritative — it reads the same status endpoint the wizard does.
 */
import { ref, computed, onMounted } from "vue";
import { periodRange } from "@/utils/datePresets.js";
import { useRouter, useRoute } from "vue-router";
import { useApp } from "@/composables/useApp.js";
import { accountingApi } from "@/utils/api.js";

const router = useRouter();
const route = useRoute();
// App-wide (it shows on every page), so it asks about the GL itself, not the page on screen.
const { canEditApp } = useApp();

const show = ref(false);
// Not on the Month End page itself: there its "Open" led to the page you were
// on (Maurizio, 2026-09-28). It returns on leaving, unless snoozed with Later.
const visible = computed(() => show.value && route.name !== "MonthEnd");
const outstanding = ref(0);
const total = ref(9);
const targetMonth = ref(previousMonth());

const monthLabel = computed(() => {
  const [y, m] = targetMonth.value.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
});

const dismissKey = computed(() => `monthEndReminderDismissed:${targetMonth.value}`);

function open() {
  show.value = false;
  router.push("/accounting/month-end");
}

function dismiss() {
  show.value = false;
  // localStorage can throw in a private window or with site data blocked —
  // a failed dismissal just means the nudge returns, which is harmless.
  try {
    // Store WHEN it was snoozed; the check on mount compares against a day.
    localStorage.setItem(dismissKey.value, String(Date.now()));
  } catch {
    /* ignore */
  }
}

/** True while a snooze set within the last 24 hours is still in force. */
const SNOOZE_MS = 24 * 60 * 60 * 1000;
function snoozed() {
  try {
    const at = Number(localStorage.getItem(dismissKey.value));
    return Number.isFinite(at) && at > 0 && Date.now() - at < SNOOZE_MS;
  } catch {
    return false; // storage unavailable → treat as not snoozed, the nudge shows
  }
}

onMounted(async () => {
  // Only people who can close should be told to close.
  // Only those who can close the month: admin role + a full gl / accounting grant (not view-only).
  if (!canEditApp("gl", "accounting")) return;
  if (snoozed()) return;
  try {
    const { data } = await accountingApi.get("/api/accounting/month-end/status", {
      params: { month: targetMonth.value },
    });
    if (data.closed) return;
    outstanding.value = data.steps.filter((s) => !s.done).length;
    total.value = data.steps.length;
    // If literally nothing has been done the month may simply not be worked yet;
    // still worth saying, since that is the case that goes unnoticed.
    show.value = outstanding.value > 0;
  } catch {
    // A failed check must never block the app or show a misleading reminder.
    show.value = false;
  }
});

/** Last month as YYYY-MM, in Dubai days (utils/datePresets.js). */
function previousMonth() {
  return periodRange("lastMonth")[0].slice(0, 7);
}
</script>
